import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Calendar, Instagram, UtensilsCrossed, Disc3 } from "lucide-react";
import { useLang } from "@/App";
import { copy, CONTACT, LOGO } from "@/i18n";
import WhatsAppIcon from "@/components/WhatsAppIcon";

const LOCALES = { it: "it-IT", en: "en-GB", de: "de-CH", fr: "fr-CH" };

const nextSaturdays = (n) => {
    const out = [];
    const d = new Date();
    d.setDate(d.getDate() + ((6 - d.getDay() + 7) % 7));
    for (let i = 0; i < n; i++) {
        out.push(new Date(d));
        d.setDate(d.getDate() + 7);
    }
    return out;
};

const SPECIAL_EVENTS = [];

const EventsPage = () => {
    const { lang, setLang } = useLang();
    const c = copy[lang].eventsPage;
    const saturdays = nextSaturdays(8);
    const fmt = new Intl.DateTimeFormat(LOCALES[lang] || "it-IT", { day: "numeric", month: "long" });

    return (
        <div data-testid="events-page" className="min-h-screen bg-[#0A0614]">
            <header className="sticky top-0 z-40 bg-noir/85 backdrop-blur-xl border-b border-white/10">
                <div className="max-w-7xl mx-auto px-4 sm:px-8 h-[72px] flex items-center justify-between gap-4">
                    <Link to="/" data-testid="events-back-home" className="flex items-center gap-3 text-neutral-300 hover:text-white transition-colors">
                        <ArrowLeft size={18} />
                        <img src={LOGO} alt="The Market Lugano" className="h-7 w-auto" />
                    </Link>
                    <div data-testid="events-lang-toggle" className="flex items-center rounded-full border border-white/15 overflow-hidden text-[11px] font-mono">
                        {["it", "en", "de", "fr"].map((l) => (
                            <button
                                key={l}
                                data-testid={`events-lang-${l}`}
                                onClick={() => setLang(l)}
                                className={`px-2.5 py-1.5 uppercase transition-colors ${lang === l ? "bg-[#7C3AED] text-white" : "text-neutral-400 hover:text-white"}`}
                            >
                                {l}
                            </button>
                        ))}
                    </div>
                </div>
            </header>

            <div className="relative h-[38vh] sm:h-[46vh] overflow-hidden">
                <img
                    src="/media/deepsociety-neon.webp"
                    alt="Deep Society × The Market Lugano"
                    className="absolute inset-0 w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0614] via-[#0A0614]/40 to-transparent" />
                <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-8 flex flex-col justify-end pb-8">
                    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
                        <span className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-[#A78BFA]">{c.label}</span>
                        <h1 className="mt-2 font-display font-extrabold uppercase tracking-tight text-3xl sm:text-5xl lg:text-6xl bg-gradient-to-r from-[#A78BFA] via-white to-[#60A5FA] bg-clip-text text-transparent">
                            {c.title}
                        </h1>
                    </motion.div>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-4 sm:px-8 py-14">
                <h2 className="font-serif italic text-2xl sm:text-3xl text-[#C4B5FD] border-b border-[#7C3AED]/25 pb-2 mb-8">
                    {c.nextSaturdays}
                </h2>
                <div className="space-y-4">
                    {saturdays.map((d, i) => (
                        <motion.div
                            key={i}
                            data-testid={`events-saturday-${i}`}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ duration: 0.5, delay: Math.min(i * 0.05, 0.3) }}
                            className="rounded-2xl border border-white/10 bg-card/60 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4"
                        >
                            <div className="flex items-center gap-4 sm:w-48 shrink-0">
                                <Calendar size={20} className="text-[#A78BFA] shrink-0" />
                                <div>
                                    <p className="font-display font-bold uppercase text-sm text-white">{c.saturday}</p>
                                    <p className="font-mono text-xs text-neutral-400 capitalize">{fmt.format(d)}</p>
                                </div>
                            </div>
                            <div className="flex-1">
                                <p className="font-heading font-bold text-sm sm:text-base bg-gradient-to-r from-[#A78BFA] to-[#60A5FA] bg-clip-text text-transparent">
                                    DEEP SOCIETY × THE MARKET
                                </p>
                                <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-400">
                                    <span className="inline-flex items-center gap-1.5"><UtensilsCrossed size={12} /> {c.dinner}</span>
                                    <span className="inline-flex items-center gap-1.5"><Disc3 size={12} /> {c.vinyl}</span>
                                </div>
                            </div>
                            <a
                                data-testid={`events-book-${i}`}
                                href={CONTACT.whatsappDs}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#7C3AED] hover:bg-[#8B5CF6] text-white text-xs font-bold uppercase tracking-widest px-5 py-2.5 transition-colors shrink-0"
                            >
                                <WhatsAppIcon size={14} />
                                {c.bookCta}
                            </a>
                        </motion.div>
                    ))}
                </div>

                <h2 className="font-serif italic text-2xl sm:text-3xl text-[#C4B5FD] border-b border-[#7C3AED]/25 pb-2 mt-16 mb-8">
                    {c.specialTitle}
                </h2>
                {SPECIAL_EVENTS.length === 0 ? (
                    <div data-testid="events-special-empty" className="rounded-2xl border border-dashed border-[#7C3AED]/40 bg-[#7C3AED]/5 p-8 text-center">
                        <p className="text-neutral-300 text-sm sm:text-base max-w-lg mx-auto">{c.specialEmpty}</p>
                        <a
                            data-testid="events-instagram-cta"
                            href={CONTACT.instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#7C3AED]/60 text-[#C4B5FD] hover:bg-[#7C3AED] hover:text-white text-xs font-bold uppercase tracking-widest px-6 py-3 transition-colors"
                        >
                            <Instagram size={15} />
                            {c.followInsta}
                        </a>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default EventsPage;
